import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";

interface IdBadgeProps {
  id: string;
  /** Label shown before the id. */
  label?: string;
  /** What this id names, for the copy affordance and the toast. */
  noun?: string;
  className?: string;
}

/**
 * A bank reference — PB-00001 or SK-00001 — rendered and copyable.
 *
 * These ids are what Studio's "Generate from Skill / Problem" import takes, so
 * they are always rendered whole. They used to be UUIDs, shown as their first
 * segment to fit; a reference is short enough to show in full, and truncating
 * one would name a different row rather than merely abbreviating it.
 */
export function IdBadge({
  id,
  label = "ID",
  noun = "problem",
  className = "",
}: IdBadgeProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!id) return null;

  const copy = async (e: React.MouseEvent) => {
    // Problem rows and skill headers have their own click handlers.
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast.success(`${noun.charAt(0).toUpperCase()}${noun.slice(1)} ID copied`);
    } catch {
      toast.error("Could not copy — select the ID and copy manually");
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border bg-muted/40 py-0.5 pl-2 pr-1 text-xs text-muted-foreground ${className}`}
    >
      <span className="shrink-0 font-semibold uppercase tracking-wide opacity-70">
        {label}
      </span>
      <code className="select-all font-mono text-[11px] leading-none">
        {id}
      </code>
      <button
        type="button"
        onClick={copy}
        title={`Copy ${noun} ID ${id}`}
        aria-label={`Copy ${noun} ID ${id}`}
        className="ml-0.5 grid h-5 w-5 shrink-0 place-items-center rounded hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {copied ? (
          <Check className="h-3 w-3 text-emerald-600" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </button>
    </span>
  );
}

export default IdBadge;
